import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import DefaultLayout from '../layout/Default';
import { connect } from 'react-redux';
import PreviewProfileUI from './PreviewProfileUI';
import Toolbar from './ProfileViewToolbar';
import NotFound from '../NotFound/NotFound';
import { Title, Meta } from 'react-head';
import { fetchProfile, fetchCompanyAds } from '../../actions/profileActions';
import { fetchUserProfiles } from '../../actions/userActions';
import {
    fetchFollowerDetails,
    followCompany,
    unfollowCompany,
} from '../../actions/followCompanyActions.js';
import analytics, * as trackingTypes from '../../actions/analyticsActions';
import Optional from 'optional-js';
import { createUrl } from '../../utils/createUrl';

class ProfileView extends Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        profile: PropTypes.object.isRequired,
        userProfiles: PropTypes.array,
    };

    // Invoked once, only on the client (not on the server), immediately after the initial rendering occurs.
    componentDidMount() {
        const id = this.props.profile.profile.id;
        this.props.dispatch(analytics(trackingTypes.VIEW_PROFILE, { id }));
        this.props.dispatch(fetchCompanyAds(id));
        this.props.dispatch(fetchUserProfiles());
        this.props.dispatch(fetchFollowerDetails(id));
    }

    static fetchData({ dispatch }, _, { id }) {
        return dispatch(fetchProfile(id));
    }

    _isProfileOwner() {
        if (!this.props.userProfiles) {
            return false;
        }

        const profileId = +this.props.profile.profile.id;
        return this.props.userProfiles.some((item) => item.id === profileId);
    }

    onFollowCompanyClick = (event) => {
        Optional.ofNullable(event)
            .filter((e) => e.preventDefault)
            .ifPresent((e) => e.preventDefault());
        this.props.dispatch(followCompany(this.props.profile.profile.id));
    };

    onUnfollowCompanyClick = (event) => {
        Optional.ofNullable(event)
            .filter((e) => e.preventDefault)
            .ifPresent((e) => e.preventDefault());
        this.props.dispatch(unfollowCompany(this.props.profile.profile.id));
    };

    _followCompanyProps() {
        return Object.assign({}, this.props.profile.followCompany, {
            onFollowCompanyClick: this.onFollowCompanyClick,
            onUnfollowCompanyClick: this.onUnfollowCompanyClick,
        });
    }

    buildDescription() {
        const { companyActiveAds, profile } = this.props.profile;
        const activeAdCount = companyActiveAds?.ads.length;
        switch (activeAdCount) {
            case 0:
                return profile.description;
            case 1:
                return `1 ledig stilling. ${profile.description}`;
            default:
                return `${activeAdCount} ledige stillinger. ${profile.description}`;
        }
    }

    render() {
        const { profile } = this.props.profile;
        const description = this.buildDescription();
        return (
            <DefaultLayout
                isProfileOwner={this._isProfileOwner()}
                profile={this.props.profile}
                secondBar={Toolbar}
            >
                {profile?.id ? (
                    <React.Fragment>
                        <Title>
                            {profile.companyName} profil og ledige stillinger |
                            FINN jobb
                        </Title>
                        <Meta content={description} name="description" />
                        <Meta content={profile.companyName} itemProp="name" />
                        <Meta content={description} itemProp="description" />
                        <Meta content={profile.background} itemProp="image" />
                        <Meta content="summary" name="twitter:card" />
                        <Meta content="@FINN_no" name="twitter:site" />
                        <Meta
                            content={`Et blikk på innsiden av ${profile.companyName}`}
                            name="twitter:title"
                        />
                        <Meta
                            content={description}
                            name="twitter:description"
                        />
                        <Meta
                            content={`Et blikk på innsiden av ${profile.companyName}`}
                            name="og:title"
                        />
                        <Meta content="website" name="og:type" />
                        <Meta content={profile.background} name="og:image" />
                        <Meta content={profile.logo} name="og:image" />
                        <Meta content={description} name="og:description" />
                        <Meta
                            content="FINN.no Bedriftsprofiler"
                            name="og:site_name"
                        />
                        {profile?.images?.images?.length ? (
                            <Meta
                                content={
                                    profile.images.images[0].image.urls.large
                                }
                                name="og:image"
                            />
                        ) : null}
                        <nav
                            aria-label="Her er du"
                            className="panel breadcrumbs"
                        >
                            <a href="/job/browse.html">Jobb</a>
                            <Link to={createUrl('companies')}>
                                Bedriftsprofiler
                            </Link>
                            <Link to={createUrl(`company/${profile.id}`)}>
                                {profile.companyName}
                            </Link>
                        </nav>
                        <PreviewProfileUI
                            companyActiveAds={
                                this.props.profile.companyActiveAds
                            }
                            dispatch={this.props.dispatch}
                            followCompany={this._followCompanyProps()}
                            profile={profile}
                        />
                    </React.Fragment>
                ) : (
                    <NotFound />
                )}
            </DefaultLayout>
        );
    }
}

export default connect((state) => ({
    profile: state.profile,
    userProfiles: state.user.userProfiles,
}))(ProfileView);
